// # Overview Stats Component for Step 1
import React, { useState, useEffect } from 'react';
import { FaTasks, FaCheckSquare, FaCalendarCheck, FaExclamationTriangle, FaUserAlt } from 'react-icons/fa';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import { SUBSCRIPTION_TYPES } from '../../../roles/admin/admin_tools/subscriptions/dialogs/constants';
import { getTargetUserId } from '../m1_access';
import './stats1_overview.css';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis } from 'recharts';

// # Define mistake types with consistent colors (same as counter1_page.js)
const MISTAKE_TYPES = [
  { id: 'lackOfAttention', label: 'Lack of Attention', color: '#FF6B6B' },
  { id: 'confusingChoices', label: 'Confusing Choices', color: '#4ECDC4' },
  { id: 'lackOfKnowledge', label: 'Lack of Knowledge', color: '#45B7D1' },
  { id: 'forgottenInfo', label: 'Forgotten Info', color: '#96CEB4' },
  { id: 'wrongInterpretation', label: 'Wrong Interpretation', color: '#C7A9C9' },
  { id: 'notSpecified', label: 'Not Specified', color: '#B8B8B8' }
];

function Stats1Overview({ isAdmin, isTutor, isMentorView, isDarkMode, targetUserId }) {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    tasks: {
      total: 0,
      completed: 0,
      byChapter: {},
      firstTaskDate: null
    },
    checklist: {
      total: 0,
      completed: 0,
      byType: {}
    },
    mentorship: {
      startDate: null,
      endDate: null,
      status: null,
      mentor: null,
      duration: null,
      durationUnit: null
    },
    mistakes: {
      total: 0,
      byType: {},
      byChapter: {}
    }
  });

  // # Fetch all required data
  useEffect(() => {
    const fetchAllData = async () => {
      // # Get the correct user ID based on roles
      const userId = targetUserId || user?.uid;
      if (!userId) return;

      try {
        setLoading(true);
        setError(null);

        // # 1. Fetch tasks data from db1_mentee collection
        const menteeTasksRef = doc(db, 'db1_mentee', userId);
        const menteeTasksSnap = await getDoc(menteeTasksRef);
        const tasksData = menteeTasksSnap.data() || {};
        
        // # Process tasks data
        let totalTasks = 0;
        let completedTasks = 0;
        const chapterStats = {};
        let firstTaskDate = null;
        
        Object.entries(tasksData.chapters || {}).forEach(([chapterId, chapterData]) => {
          const chapterTasks = Object.values(chapterData.tasks || {});
          const chapterTotal = chapterTasks.length;
          const chapterCompleted = chapterTasks.filter(task => task.done).length;
          
          // # Find earliest task date
          chapterTasks.forEach(task => {
            if (task.date) {
              const taskDate = new Date(task.date);
              if (!firstTaskDate || taskDate < firstTaskDate) {
                firstTaskDate = taskDate;
              }
            }
          });
          
          chapterStats[chapterId] = {
            total: chapterTotal,
            completed: chapterCompleted,
            name: chapterData.metadata?.chapterName || chapterId
          };
          
          totalTasks += chapterTotal;
          completedTasks += chapterCompleted;
        });

        // # 2. Fetch checklist data from s1_mentee collection
        const menteeChecklistRef = doc(db, 's1_mentee', userId);
        const menteeChecklistSnap = await getDoc(menteeChecklistRef);
        const checklistData = menteeChecklistSnap.data() || {};
        
        // # Process quicktasks data
        const quicktasks = checklistData.quicktasks || {};
        const quicktaskStats = {
          total: Object.keys(quicktasks).length,
          completed: Object.values(quicktasks).filter(task => task.completed).length,
          byType: Object.values(quicktasks).reduce((acc, task) => {
            acc[task.type] = acc[task.type] || { total: 0, completed: 0 };
            acc[task.type].total++;
            if (task.completed) acc[task.type].completed++;
            return acc;
          }, {})
        };

        // # 3. Fetch subscription data for mentorship
        const subscriptionRef = doc(db, 'subscriptions', userId);
        const subscriptionSnap = await getDoc(subscriptionRef);
        let mentorshipData = { 
          startDate: null,
          endDate: null,
          status: null,
          mentor: null,
          duration: null,
          durationUnit: null
        };
        
        if (subscriptionSnap.exists()) {
          const subData = subscriptionSnap.data();
          const step1Subscription = subData.subscriptions?.[SUBSCRIPTION_TYPES.STEP1_MENTORSHIP.id];
          
          if (step1Subscription) {
            mentorshipData = {
              startDate: step1Subscription.startDate,
              endDate: step1Subscription.endDate,
              status: step1Subscription.status,
              mentor: step1Subscription.mentor,
              duration: step1Subscription.metadata?.duration,
              durationUnit: step1Subscription.metadata?.durationUnit
            };
          }
        }

        // # 4. Fetch mistakes data
        const menteeMistakesRef = doc(db, 's1_mentee', userId);
        const menteeMistakesSnap = await getDoc(menteeMistakesRef);
        const mistakesData = menteeMistakesSnap.data()?.mistakeCounter || {};
        
        // # Process mistakes data
        const mistakesByType = {};
        const mistakesByChapter = {};
        let totalMistakes = 0;

        Object.entries(mistakesData.chapters || {}).forEach(([chapterId, chapterData]) => {
          // # Aggregate by chapter
          mistakesByChapter[chapterId] = {
            total: chapterData.totalMistakes || 0,
            types: chapterData.mistakes || {}
          };
          totalMistakes += chapterData.totalMistakes || 0;

          // # Aggregate by type
          Object.entries(chapterData.mistakes || {}).forEach(([type, count]) => {
            mistakesByType[type] = (mistakesByType[type] || 0) + count;
          });
        });

        // # Update state with all fetched data
        setData({
          tasks: {
            total: totalTasks,
            completed: completedTasks,
            byChapter: chapterStats,
            firstTaskDate
          },
          checklist: quicktaskStats,
          mentorship: mentorshipData,
          mistakes: {
            total: totalMistakes,
            byType: mistakesByType,
            byChapter: mistakesByChapter
          }
        });

      } catch (err) {
        console.error('Error fetching overview data:', err);
        setError('Failed to load overview data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [user?.uid, targetUserId]);

  // # Handle section navigation
  const handleSectionClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="s1_ovw_loading">
        <div className="s1_ovw_loading_spinner"></div>
        <p>Loading overview data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="s1_ovw_error">
        <FaExclamationTriangle className="s1_ovw_error_icon" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={`s1_ovw_container ${isDarkMode ? 's1_ovw_dark-mode' : ''}`}>
      {/* Quick Navigation */}
      <div className="s1_ovw_quick_nav">
        <button 
          className="s1_ovw_nav_button"
          onClick={() => handleSectionClick('timeline')}
        >
          <FaCalendarCheck className="s1_ovw_nav_icon" />
          Timeline
        </button>
        <button 
          className="s1_ovw_nav_button"
          onClick={() => handleSectionClick('tasks')}
        >
          <FaTasks className="s1_ovw_nav_icon" />
          Tasks
        </button>
        <button 
          className="s1_ovw_nav_button"
          onClick={() => handleSectionClick('mistakes')}
        >
          <FaExclamationTriangle className="s1_ovw_nav_icon" />
          Mistakes
        </button>
        <button 
          className="s1_ovw_nav_button"
          onClick={() => handleSectionClick('checklist')}
        >
          <FaCheckSquare className="s1_ovw_nav_icon" />
          Checklist
        </button>
      </div>

      {/* Mentorship Timeline Section */}
      <section id="timeline" className="s1_ovw_section">
        <div className="s1_ovw_section_header">
          <FaCalendarCheck className="s1_ovw_section_icon" />
          <h2>Mentorship Timeline</h2>
        </div>
        <div className="s1_ovw_content">
          <div className="s1_ovw_timeline">
            {data.mentorship.startDate ? (
              <>
                <div className="s1_ovw_timeline_item">
                  <span className="s1_ovw_timeline_label">Status</span>
                  <span className={`s1_ovw_timeline_value s1_ovw_status-${data.mentorship.status}`}>
                    {data.mentorship.status?.charAt(0).toUpperCase() + data.mentorship.status?.slice(1)}
                  </span>
                </div>
                
                <div className="s1_ovw_timeline_item">
                  <span className="s1_ovw_timeline_label">Start Date</span>
                  <span className="s1_ovw_timeline_value">
                    {new Date(data.mentorship.startDate).toLocaleDateString()}
                  </span>
                </div>

                <div className="s1_ovw_timeline_item">
                  <span className="s1_ovw_timeline_label">End Date</span>
                  <span className="s1_ovw_timeline_value">
                    {new Date(data.mentorship.endDate).toLocaleDateString()}
                  </span>
                </div>

                {data.mentorship.mentor && (
                  <div className="s1_ovw_timeline_item">
                    <span className="s1_ovw_timeline_label">
                      <FaUserAlt className="s1_ovw_mentor_icon" />
                      Mentor
                    </span>
                    <span className="s1_ovw_timeline_value">
                      {data.mentorship.mentor.name} 
                      <span className="s1_ovw_mentor_role">
                        ({data.mentorship.mentor.displayRole || data.mentorship.mentor.role})
                      </span>
                    </span>
                  </div>
                )}

                {data.tasks.firstTaskDate && (
                  <div className="s1_ovw_timeline_item">
                    <span className="s1_ovw_timeline_label">First Task</span>
                    <span className="s1_ovw_timeline_value">
                      {new Date(data.tasks.firstTaskDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {data.mentorship.duration && data.mentorship.durationUnit && (
                  <div className="s1_ovw_timeline_item">
                    <span className="s1_ovw_timeline_label">Duration</span>
                    <span className="s1_ovw_timeline_value">
                      {data.mentorship.duration} {data.mentorship.durationUnit}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="s1_ovw_timeline_empty">
                No mentorship data available
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Task Completion Section */}
      <section id="tasks" className="s1_ovw_section">
        <div className="s1_ovw_section_header">
          <FaTasks className="s1_ovw_section_icon" />
          <h2>Task Completion</h2>
        </div>
        <div className="s1_ovw_content">
          <div className="s1_ovw_overall_stats">
            <div className="s1_ovw_stat">
              <span className="s1_ovw_stat_label">Total Tasks</span>
              <span className="s1_ovw_stat_value">{data.tasks.total}</span>
            </div>
            <div className="s1_ovw_stat">
              <span className="s1_ovw_stat_label">Completed</span>
              <span className="s1_ovw_stat_value">{data.tasks.completed}</span>
            </div>
            <div className="s1_ovw_stat">
              <span className="s1_ovw_stat_label">Completion Rate</span>
              <span className="s1_ovw_stat_value">
                {data.tasks.total > 0 ? Math.round((data.tasks.completed / data.tasks.total) * 100) : 0}%
              </span>
            </div>
          </div>
          <div className="s1_ovw_chapter_stats">
            {Object.entries(data.tasks.byChapter).map(([chapterId, stats]) => (
              <div key={chapterId} className="s1_ovw_chapter_item">
                <span className="s1_ovw_chapter_name">{stats.name}</span>
                <div className="s1_ovw_chapter_progress">
                  <div 
                    className="s1_ovw_chapter_progress_bar"
                    style={{ width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="s1_ovw_chapter_completion">
                  {stats.completed}/{stats.total}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mistakes Analysis Section */}
      <section id="mistakes" className="s1_ovw_section">
        <div className="s1_ovw_section_header">
          <FaExclamationTriangle className="s1_ovw_section_icon" />
          <h2>Mistakes Analysis</h2>
        </div>
        <div className="s1_ovw_content">
          <div className="s1_ovw_mistakes_grid">
            {/* Left Column - Stats */}
            <div className="s1_ovw_mistakes_stats">
              {/* Overall Stats */}
              <div className="s1_ovw_overall_stats">
                <div className="s1_ovw_stat">
                  <span className="s1_ovw_stat_label">Total Mistakes</span>
                  <span className="s1_ovw_stat_value">{data.mistakes.total}</span>
                </div>
              </div>

              {/* Mistakes by Type */}
              <div className="s1_ovw_mistakes_by_type">
                {MISTAKE_TYPES.map(type => {
                  const count = data.mistakes.byType[type.id] || 0;
                  const percentage = data.mistakes.total > 0 
                    ? ((count / data.mistakes.total) * 100).toFixed(1) 
                    : 0;
                  
                  return (
                    <div key={type.id} className="s1_ovw_mistake_type">
                      <span className="s1_ovw_mistake_type_name">{type.label}</span>
                      <span className="s1_ovw_mistake_type_count">{count}</span>
                      <span className="s1_ovw_mistake_type_percentage">({percentage}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column - Chart */}
            <div className="s1_ovw_mistakes_chart">
              <ResponsiveContainer width="100%" height={400}>
                <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <Pie
                    data={MISTAKE_TYPES.map(type => ({
                      name: type.label,
                      value: data.mistakes.byType[type.id] || 0,
                      color: type.color
                    })).filter(item => item.value > 0)}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="40%"
                    outerRadius={90}
                    label={({ name, value, percent }) => 
                      `${value} (${(percent * 100).toFixed(1)}%)`
                    }
                  >
                    {MISTAKE_TYPES.map((type, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={type.color}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                      borderRadius: '8px',
                      border: `1px solid ${isDarkMode ? '#333333' : '#e2e8f0'}`
                    }}
                    itemStyle={{ 
                      color: isDarkMode ? '#e0e0e0' : '#333'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom"
                    align="center"
                    layout="horizontal"
                    wrapperStyle={{
                      paddingTop: "20px",
                      width: "100%",
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: "10px"
                    }}
                    formatter={(value, entry) => (
                      <span className="s1_ovw_chart_legend_item" style={{ color: isDarkMode ? '#e0e0e0' : entry.color }}>
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chapter Breakdown */}
          <div className="s1_ovw_chapter_mistakes">
            {Object.entries(data.mistakes.byChapter)
              .sort(([, a], [, b]) => b.total - a.total)
              .map(([chapterId, stats]) => {
                // Prepare data for the mini bar chart
                const barData = MISTAKE_TYPES.map(type => ({
                  name: type.label.split(' ')[0], // Use first word for compact display
                  fullName: type.label,
                  value: stats.types[type.id] || 0,
                  color: type.color
                })).filter(item => item.value > 0);

                return (
                  <div key={chapterId} className="s1_ovw_chapter_mistake">
                    <div className="s1_ovw_chapter_content">
                      <div className="s1_ovw_chapter_name">
                        {chapterId}
                        <span className="s1_ovw_mistake_count">{stats.total}</span>
                      </div>
                      <div className="s1_ovw_mistake_types">
                        {Object.entries(stats.types)
                          .sort(([, a], [, b]) => b - a)
                          .map(([type, count]) => {
                            const mistakeType = MISTAKE_TYPES.find(t => t.id === type);
                            return (
                              <div key={type} className="s1_ovw_mistake_type_small">
                                <span 
                                  className="s1_ovw_mistake_type_dot" 
                                  data-type={type}
                                  style={{ backgroundColor: mistakeType?.color }}
                                ></span>
                                <span className="s1_ovw_mistake_type_label">
                                  {mistakeType?.label || type.replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                                <span className="s1_ovw_mistake_type_value">{count}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    <div className="s1_ovw_chapter_chart">
                      <ResponsiveContainer width="100%" height={120}>
                        <BarChart 
                          data={barData} 
                          layout="vertical" 
                          margin={{ top: 5, right: 5, bottom: 5, left: 50 }}
                        >
                          <XAxis type="number" hide />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            tick={{ 
                              fill: isDarkMode ? '#e0e0e0' : '#666',
                              fontSize: '0.75rem'
                            }}
                            width={45}
                          />
                          <Tooltip
                            contentStyle={{ 
                              backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                              borderRadius: '4px',
                              border: `1px solid ${isDarkMode ? '#333333' : '#e2e8f0'}`,
                              fontSize: '0.75rem',
                              padding: '4px 8px'
                            }}
                            formatter={(value, name, props) => [value, props.payload.fullName]}
                            labelStyle={{ display: 'none' }}
                          />
                          <Bar dataKey="value" minPointSize={2}>
                            {barData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* Checklist Completion Section */}
      <section id="checklist" className="s1_ovw_section">
        <div className="s1_ovw_section_header">
          <FaCheckSquare className="s1_ovw_section_icon" />
          <h2>Checklist Completion</h2>
        </div>
        <div className="s1_ovw_content">
          <div className="s1_ovw_overall_stats">
            <div className="s1_ovw_stat">
              <span className="s1_ovw_stat_label">Total Tasks</span>
              <span className="s1_ovw_stat_value">{data.checklist.total}</span>
            </div>
            <div className="s1_ovw_stat">
              <span className="s1_ovw_stat_label">Completed</span>
              <span className="s1_ovw_stat_value">{data.checklist.completed}</span>
            </div>
          </div>
          <div className="s1_ovw_type_stats">
            {Object.entries(data.checklist.byType).map(([type, stats]) => (
              <div key={type} className="s1_ovw_type_item">
                <span className="s1_ovw_type_name">{type}</span>
                <div className="s1_ovw_type_progress">
                  <div 
                    className="s1_ovw_type_progress_bar"
                    style={{ width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="s1_ovw_type_completion">
                  {stats.completed}/{stats.total}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Stats1Overview;
